import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

const base = () => `${config.apiUrl}/training-standard`;
const withSortQuery = (
  path: string,
  params?: { sortBy?: string; sortOrder?: "asc" | "desc" },
) => {
  const qp = new URLSearchParams();
  if (params?.sortBy) qp.set("sortBy", params.sortBy);
  if (params?.sortOrder) qp.set("sortOrder", params.sortOrder);
  const query = qp.toString();
  return query ? `${path}?${query}` : path;
};

export type ChecklistAlertStatus = "none" | "due_soon" | "overdue";

export type HubSummaryDTO = {
  totals: {
    sop: number;
    flow: number;
    standard: number;
    checklist: number;
    kpi: number;
  };
  checklistProgress: number;
  checklistAlerts: {
    overdue: number;
    dueSoon: number;
  };
  checklistNotifications: Array<{
    id: string;
    title: string;
    alertStatus: "overdue" | "due_soon";
    submissionDueAt: string | null;
  }>;
};

export type ChecklistRelatedRef = { id: string; title: string };

export type ChecklistDetailDTO = {
  id: string;
  title: string;
  description: string | null;
  requireAllItems: boolean;
  requirePhoto: boolean;
  requireNotesOnFail: boolean;
  scheduleType: "daily" | "opening" | "closing" | "scheduled";
  scheduleFrequency: "daily" | "weekly" | "monthly" | null;
  scheduleDayOfWeek: number | null;
  scheduleDayOfMonth: number | null;
  executionId: string | null;
  executionStatus: "pending" | "in_progress" | "completed" | "cancelled";
  executionStartedAt: string | null;
  executionCompletedAt: string | null;
  executionNotes: string | null;
  submissionDueAt: string | null;
  alertStatus: ChecklistAlertStatus;
  relatedSops: ChecklistRelatedRef[];
  relatedFlows: ChecklistRelatedRef[];
  items: Array<{
    id: string;
    title: string;
    description: string | null;
    isRequired: boolean;
    allowNotes: boolean;
    order: number;
    isCompleted: boolean;
    notes: string | null;
    photoUrl: string | null;
  }>;
};

export const trainingStandardApi = {
  getHubSummary(): Promise<HubSummaryDTO> {
    return fetcher.get(`${base()}/hub`);
  },
  listSop(params?: {
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }): Promise<{ data: Array<Record<string, unknown>> }> {
    return fetcher.get(withSortQuery(`${base()}/sop`, params));
  },
  getSop(id: string): Promise<Record<string, unknown>> {
    return fetcher.get(`${base()}/sop/${id}`);
  },
  listFlows(params?: {
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }): Promise<{ data: Array<Record<string, unknown>> }> {
    return fetcher.get(withSortQuery(`${base()}/flows`, params));
  },
  getFlow(id: string): Promise<Record<string, unknown>> {
    return fetcher.get(`${base()}/flows/${id}`);
  },
  listStandards(params?: {
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }): Promise<{ data: Array<Record<string, unknown>> }> {
    return fetcher.get(withSortQuery(`${base()}/standards`, params));
  },
  getStandard(id: string): Promise<Record<string, unknown>> {
    return fetcher.get(`${base()}/standards/${id}`);
  },
  listChecklists(params?: {
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }): Promise<{ data: Array<Record<string, unknown>> }> {
    return fetcher.get(withSortQuery(`${base()}/checklists`, params));
  },
  getChecklist(id: string): Promise<ChecklistDetailDTO> {
    return fetcher.get(`${base()}/checklists/${id}`);
  },
  patchChecklistExecutionNotes(
    checklistId: string,
    payload: { notes: string | null },
  ): Promise<ChecklistDetailDTO> {
    return fetcher.patch(`${base()}/checklists/${checklistId}/notes`, payload);
  },
  updateChecklistItem(
    checklistId: string,
    payload: {
      itemId: string;
      isCompleted: boolean;
      notes?: string | null;
      photoUrl?: string | null;
    },
  ): Promise<ChecklistDetailDTO> {
    return fetcher.patch(`${base()}/checklists/${checklistId}/items`, payload);
  },
  completeChecklist(checklistId: string): Promise<ChecklistDetailDTO> {
    return fetcher.post(`${base()}/checklists/${checklistId}/complete`);
  },
  listKpi(params?: {
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }): Promise<{ data: Array<Record<string, unknown>> }> {
    return fetcher.get(withSortQuery(`${base()}/kpi`, params));
  },
};
